import { yupResolver } from '@hookform/resolvers/yup'
import { Box, Button, Stack } from '@mui/material'
import { useMutation } from '@tanstack/react-query'
import { Page } from 'components/Shared/Page'
import { useApiClient } from 'hooks/useApiClient'
import { useForm, type Control } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { useSnackbar } from 'stores/useSnackbar'
import { object, string } from 'yup'
import { PageHeader } from 'components/Shared/PageHeader'
import { TextField } from 'components/Shared/TextField'
import { FormFieldErrorMessage } from 'components/FormFieldErrorMessage'
import { isPresent } from '@jjvgaming/player-payback-library'

const today = new Date()
today.setHours(0, 0, 0, 0)

const resourceSchema = object().shape({
  title: string().required('Must enter a title'),
})

type FormSchemaType<T> = T extends Control<infer R> ? R : never

export const ResourcesNewPage = () => {
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(resourceSchema),
  })
  const { post } = useApiClient()
  const setMessage = useSnackbar((state) => state.setMessage)
  const navigate = useNavigate()

  const createResource = useMutation({
    mutationKey: ['resources/create'],
    mutationFn: async (data: FormSchemaType<typeof control>) => {
      const response = await post('/admin/resources', {
        params: { header: undefined },
        body: {
          title: data.title,
          resourceCategoryId: undefined,
        },
      })
      return response
    },
    onSuccess: async (response) => {
      setMessage('Resource successfully created', 'success', {
        vertical: 'top',
        horizontal: 'right',
      })
      if (response.data?.id) {
        navigate(`/JJUpdates/Resources/${String(response.data.id)}`)
      } else {
        navigate(`/JJUpdates/Resources`)
      }
    },
    onError: async () => {
      setMessage('Error creating resource', 'error', {
        vertical: 'top',
        horizontal: 'right',
      })
    },
  })

  const createResourceWrapper = handleSubmit((data) => {
    createResource.mutate(data)
  })

  return (
    <Page
      header={
        <PageHeader
          title="Create a New Resource"
          backPath="/JJUpdates/Resources"
          backText="Resources"
          isSecondary={true}
        />
      }
    >
      <form onSubmit={createResourceWrapper}>
        <Stack spacing={4}>
          <Box>
            <TextField
              required
              error={isPresent(errors.title)}
              label="Title"
              placeholder="Enter resource title"
              {...register('title')}
            />
            {errors.title?.message && (
              <FormFieldErrorMessage message={errors.title.message} />
            )}
          </Box>
          <Stack direction="row" spacing={2}>
            <Button
              type="submit"
              variant="contained"
              disabled={createResource.isPending}
            >
              Submit
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                navigate('/JJUpdates/Resources')
              }}
            >
              Cancel
            </Button>
          </Stack>
        </Stack>
      </form>
    </Page>
  )
}
